import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TIERS, PRODUCTS, productImage } from "@/data/catalogue";
import { TierBadge } from "@/components/TierBadge";
import { Reveal } from "@/components/Reveal";
import { cn } from "@/lib/utils";

export default function Catalogue() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTier = searchParams.get("tier") || "all";
  const [active, setActive] = useState(activeTier);

  const setTier = (id) => {
    setActive(id);
    if (id === "all") setSearchParams({});
    else setSearchParams({ tier: id });
  };

  const products = useMemo(
    () => (active === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.tier === active)),
    [active]
  );

  return (
    <div className="mx-auto max-w-7xl px-5 py-14 lg:px-8 lg:py-20">
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest-xs text-[hsl(var(--brand))]">Catalogue</div>
        <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
          Governance technology, by status tier.
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Every product states its governance boundaries, who it's best for, and indicative "From"
          pricing. Roadmap products are register-interest only. All pricing excludes GST and is subject
          to scope.
        </p>
      </div>

      <div className="mt-12 grid gap-10 lg:grid-cols-[240px_1fr]">
        {/* Filter sidebar */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="text-xs uppercase tracking-widest-xs text-muted-foreground">Filter by tier</div>
          <div className="mt-4 flex flex-wrap gap-2 lg:flex-col lg:items-start">
            <button
              data-testid="filter-all"
              onClick={() => setTier("all")}
              className={cn(
                "border px-4 py-2 text-left text-sm transition-colors duration-200 lg:w-full",
                active === "all"
                  ? "border-foreground bg-foreground text-background"
                  : "border-border hover:bg-secondary"
              )}
            >
              All products ({PRODUCTS.length})
            </button>
            {TIERS.map((t) => (
              <button
                key={t.id}
                data-testid={`filter-${t.id}`}
                onClick={() => setTier(t.id)}
                className={cn(
                  "border px-4 py-2 text-left text-sm transition-colors duration-200 lg:w-full",
                  active === t.id
                    ? "border-foreground bg-foreground text-background"
                    : "border-border hover:bg-secondary"
                )}
              >
                {t.label} ({PRODUCTS.filter((p) => p.tier === t.id).length})
              </button>
            ))}
          </div>
        </aside>

        {/* Product list */}
        <div data-testid="catalogue-list" className="border-x border-t border-border">
          {products.map((p, i) => (
            <Reveal key={p.slug} delay={Math.min(i, 6) * 55}>
            <Link
              to={`/catalogue/${p.slug}`}
              data-testid={`catalogue-item-${p.slug}`}
              className="group flex flex-col gap-4 border-b border-border p-6 transition-colors duration-200 hover:bg-secondary lg:flex-row lg:items-start lg:gap-8 lg:p-8"
            >
              <div className="hidden shrink-0 overflow-hidden border border-border lg:block lg:h-24 lg:w-36">
                <img
                  src={productImage(p.slug)}
                  alt=""
                  aria-hidden="true"
                  className="h-full w-full object-cover opacity-80 transition-opacity duration-200 group-hover:opacity-100"
                />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <TierBadge tierId={p.tier} />
                </div>
                <h3 className="mt-4 font-display text-xl font-medium leading-tight lg:text-2xl">
                  {p.name}
                  {p.tm && <sup className="ml-0.5 text-xs">™</sup>}
                </h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">{p.tagline}</p>
                <p className="mt-4 text-xs text-muted-foreground">
                  <span className="uppercase tracking-widest-xs">Best for</span> — {p.bestFor}
                </p>
              </div>
              <div className="flex items-center justify-between gap-4 lg:w-56 lg:flex-col lg:items-end lg:text-right">
                <div>
                  <div className="text-[10px] uppercase tracking-widest-xs text-muted-foreground">
                    {p.priceType === "interest" ? "Availability" : "From"}
                  </div>
                  <div className="mt-1 font-mono text-sm font-medium text-[hsl(var(--brand))]">
                    {p.priceType === "interest" ? "Register interest" : p.price.split(";")[0]}
                  </div>
                </div>
                <ArrowRight
                  className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1 group-hover:text-foreground"
                  strokeWidth={1.5}
                />
              </div>
            </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
